import 'css/src/components/common/ui/feature-lock-modal';

import React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Immutable from 'immutable';
import classNames from 'classnames';

import { Urls } from 'js/src/client';
import { BUTTON_VARIANTS, PrimaryButton } from 'js/src/components/common/ui/Button';
import Modal, { MODAL_SIZES } from 'js/src/components/common/ui/modal';
import type { FeatureLockModalContext } from 'js/src/data/modals';
import {
    getModalContext,
    hideModal,
    isFeatureLockModalShowing,
    LOCKED_FEATURES,
} from 'js/src/data/modals';
import { MAX_AI_VOICEOVER_WORD_COUNT } from 'js/src/data/script-composer';
import { trackUpsellClicked } from 'js/src/libs/analytics-utils';
import type { ImmutableMap } from 'js/src/libs/consts';
import {
    HUBSPOT_BOOK_CHAT_LINK,
    HUBSPOT_MEETING_SCRIPT_TAG_ID,
    HUBSPOT_MEETING_SCRIPT_TAG_SRC,
    PLACEHOLDER_IMAGE_URL,
} from 'js/src/libs/consts';
import { pluralize } from 'js/src/libs/custom-utils';
import type { AppDispatch, RootState } from 'js/src/libs/RootState';

enum FEATURE_LOCK_CTA {
    VIEW_PRICING = 'VIEW_PRICING',
    CONTACT_US = 'CONTACT_US',
    CONFIRMATION = 'CONFIRMATION',
}

enum FEATURE_LOCK_MODAL_STYLE {
    VERTICAL = 'VERTICAL',
    HORIZONTAL = 'HORIZONTAL',
}

type FEATURE_LOCK_DATA = {
    headline: string;
    renderDescription: (props: PropsFromRedux) => React.ReactNode;
    image_url: string;
    call_to_action: FEATURE_LOCK_CTA;
    modal_style: FEATURE_LOCK_MODAL_STYLE;
    modal_size?: MODAL_SIZES;
};

const FEATURE_LOCK_MODAL_CTA_TEXT: Record<FEATURE_LOCK_CTA, string> = {
    [FEATURE_LOCK_CTA.VIEW_PRICING]: 'View pricing options',
    [FEATURE_LOCK_CTA.CONTACT_US]: 'Contact us',
    [FEATURE_LOCK_CTA.CONFIRMATION]: 'Got it!',
};

const FEATURE_LOCK_MODAL_DATA: Record<LOCKED_FEATURES, FEATURE_LOCK_DATA> = {
    /**
     * Resolution
     */
    [LOCKED_FEATURES.RENDER_720P]: {
        headline: 'Elevate your visuals',
        renderDescription: () => (
            <>
                Grab attention with razor-sharp videos at 720p resolution.{' '}
                <b>Available with upgraded plans.</b>
            </>
        ),
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/upsell_modals/ModalB8-ElevateVisuals.png',
        call_to_action: FEATURE_LOCK_CTA.VIEW_PRICING,
        modal_style: FEATURE_LOCK_MODAL_STYLE.VERTICAL,
    },
    [LOCKED_FEATURES.RENDER_1080P]: {
        headline: 'Elevate your visuals',
        renderDescription: () => (
            <>
                Grab attention with razor-sharp videos at 1080p resolution.{' '}
                <b>Available with upgraded plans.</b>
            </>
        ),
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/upsell_modals/ModalB8-ElevateVisuals.png',
        call_to_action: FEATURE_LOCK_CTA.VIEW_PRICING,
        modal_style: FEATURE_LOCK_MODAL_STYLE.VERTICAL,
    },
    /**
     * Media
     */
    [LOCKED_FEATURES.UNLIMITED_PREMIUM_MEDIA]: {
        headline: 'You’re using premium media 💎',
        renderDescription: () => (
            <>
                You&apos;ve got good taste! Millions of premium photos and videos from Shutterstock{' '}
                <b>are available on an upgraded plan.</b>
            </>
        ),
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/upsell_modals/ModalB1-UnlockPremiumMedia.png',
        call_to_action: FEATURE_LOCK_CTA.VIEW_PRICING,
        modal_style: FEATURE_LOCK_MODAL_STYLE.VERTICAL,
    },
    [LOCKED_FEATURES.MARKETPLACE_MEDIA]: {
        headline: 'Access ultra-high quality media',
        renderDescription: () => (
            <>
                Get the best media available on the web—from both the Getty and Shutterstock
                catalogues. <b>Available with upgraded plans.</b>
            </>
        ),
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/Access-Ultra-High-Quality-Media_v2%201.png',
        call_to_action: FEATURE_LOCK_CTA.VIEW_PRICING,
        modal_style: FEATURE_LOCK_MODAL_STYLE.VERTICAL,
    },
    [LOCKED_FEATURES.HIT_MEDIA_CREDIT_LIMIT]: {
        headline: "You've run out of marketplace media credits",
        renderDescription: (props) => {
            const has_access_to_additional_credits =
                SERVER.USER.plan.toLowerCase().indexOf('business') >= 0 ||
                SERVER.USER.plan.toLowerCase().indexOf('team') >= 0;

            const premiumMediaCreditsLeft = props.modalContext?.get('premiumMediaCreditsLeft');
            if (has_access_to_additional_credits && typeof premiumMediaCreditsLeft === 'number') {
                return (
                    <>
                        You will have <b>{premiumMediaCreditsLeft}</b> marketplace media{' '}
                        {pluralize(premiumMediaCreditsLeft, 'credit')} remaining.{' '}
                        <a
                            href="https://help.lumen5.com/en/article/all-about-shutterstock-media-libraries-credits-1vexwek/"
                            target="_blank"
                            rel="noreferrer"
                        >
                            Learn&nbsp;more
                        </a>
                    </>
                );
            }

            return 'Upgrade your account to access more marketplace media';
        },
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/upsell_modals/MarketplaceMediaCredits.png',
        call_to_action: FEATURE_LOCK_CTA.CONTACT_US,
        modal_style: FEATURE_LOCK_MODAL_STYLE.VERTICAL,
    },
    [LOCKED_FEATURES.HIT_IMAGE_TRANSFORM_MEDIA_CREDIT_LIMIT]: {
        headline: "You've run out of marketplace media credits",
        renderDescription: () =>
            'Upgrade your plan to access more credits and keep editing marketplace media with AI.',
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/upsell_modals/MarketplaceMediaCredits.png',
        call_to_action: FEATURE_LOCK_CTA.CONTACT_US,
        modal_style: FEATURE_LOCK_MODAL_STYLE.VERTICAL,
    },
    /**
     * Style: Watermark
     */
    [LOCKED_FEATURES.CUSTOM_WATERMARK]: {
        headline: 'Make your mark! ',
        renderDescription: () => (
            <>
                Increase brand awareness by adding a custom watermark to your videos.{' '}
                <b>Available with upgraded plans.</b>
            </>
        ),
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/upsell_modals/ModalB7-MakeYourMark.png',
        call_to_action: FEATURE_LOCK_CTA.VIEW_PRICING,
        modal_style: FEATURE_LOCK_MODAL_STYLE.VERTICAL,
    },
    /**
     * Style: Fonts
     */
    [LOCKED_FEATURES.PREMIUM_FONTS]: {
        headline: 'Customize your fonts',
        renderDescription: () => (
            <>
                Dive in and explore 80+ fonts to make your video uniquely you.{' '}
                <b>Available with upgraded plans.</b>
            </>
        ),
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/upsell_modals/ModalB5-CustomizeFonts.png',
        call_to_action: FEATURE_LOCK_CTA.VIEW_PRICING,
        modal_style: FEATURE_LOCK_MODAL_STYLE.VERTICAL,
    },
    [LOCKED_FEATURES.CUSTOM_FONT]: {
        headline: 'Upload custom fonts',
        renderDescription: () => (
            <>
                Need a specific font that&apos;s uniquely you?
                <br />
                Upgrade your plan to upload custom fonts.
            </>
        ),
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/upsell_modals/ModalB6-UploadCustomFonts.png',
        call_to_action: FEATURE_LOCK_CTA.VIEW_PRICING,
        modal_style: FEATURE_LOCK_MODAL_STYLE.VERTICAL,
    },
    /**
     * Style: Colors
     */
    [LOCKED_FEATURES.CUSTOM_COLORS]: {
        headline: 'Customize your colours',
        renderDescription: () => (
            <>
                Craft a unique colour palette to your video with customizable colours.{' '}
                <b>Available with upgraded plans.</b>
            </>
        ),
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/upsell_modals/ModalB2-CustomizeColors.png',
        call_to_action: FEATURE_LOCK_CTA.VIEW_PRICING,
        modal_style: FEATURE_LOCK_MODAL_STYLE.VERTICAL,
    },
    /**
     * Workspaces
     */
    [LOCKED_FEATURES.MULTIPLE_WORKSPACES]: {
        headline: 'Level up your workspace game!',
        renderDescription: () => (
            <>
                Elevate your workflow by creating separate workspaces for clients or projects.{' '}
                <b>Available with upgraded plans.</b>
            </>
        ),
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/upsell_modals/Modal1-ElevateWorkspace.png',
        call_to_action: FEATURE_LOCK_CTA.VIEW_PRICING,
        modal_style: FEATURE_LOCK_MODAL_STYLE.VERTICAL,
    },
    [LOCKED_FEATURES.HIT_MAX_WORKSPACES]: {
        headline: 'You’re keeping things busy!',
        renderDescription: () =>
            "Looks like you've reached your workspace limit - no worries though! Reach out to our team to create more workspaces.",
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/upsell_modals/Modal2-WorkspaceLimit.png',
        call_to_action: FEATURE_LOCK_CTA.CONTACT_US,
        modal_style: FEATURE_LOCK_MODAL_STYLE.VERTICAL,
    },
    /**
     * Brand kits
     */
    [LOCKED_FEATURES.BRAND_KITS]: {
        headline: 'Craft your unique brand',
        renderDescription: () => (
            <>
                Boost brand recognition with consistent fonts, logos, and colors for every video.{' '}
                <b>Available with upgraded plans.</b>
            </>
        ),
        image_url: 'https://cdn.lumen5.com/lumen5-site-images/Craft-Unique-Brands_v2%201.png',
        call_to_action: FEATURE_LOCK_CTA.VIEW_PRICING,
        modal_style: FEATURE_LOCK_MODAL_STYLE.VERTICAL,
    },
    [LOCKED_FEATURES.BRAND_KITS_LIMIT]: {
        headline: 'Effortlessly handle brand guidelines',
        renderDescription: () => (
            <>
                You&apos;ve hit your plan&apos;s brand kit limit. Manage multiple clients or brands
                with separate brand guidelines. <b>Available with upgraded plans.</b>
            </>
        ),
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/upsell_modals/Modal8-ManageBrandguidelines.png',
        call_to_action: FEATURE_LOCK_CTA.VIEW_PRICING,
        modal_style: FEATURE_LOCK_MODAL_STYLE.VERTICAL,
    },
    [LOCKED_FEATURES.ENTERPRISE_BRAND_KITS]: {
        headline: '',
        renderDescription: () =>
            'Brand kits are not currently available on your account. Speak to your team admin for more details.',
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/upsell_modals/Modal8-ManageBrandguidelines.png',
        call_to_action: FEATURE_LOCK_CTA.CONFIRMATION,
        modal_style: FEATURE_LOCK_MODAL_STYLE.VERTICAL,
    },
    [LOCKED_FEATURES.BRAND_KITS_LIMIT_CONTACT_US]: {
        headline: 'Branding Extraordinaire Alert!',
        renderDescription: () =>
            "You've hit the 3 brand kits limit. Get in touch with our team to unlock more brand kits.",
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/upsell_modals/Modal8-ManageBrandguidelines.png',
        call_to_action: FEATURE_LOCK_CTA.CONTACT_US,
        modal_style: FEATURE_LOCK_MODAL_STYLE.VERTICAL,
    },

    /**
     * Translation
     */
    [LOCKED_FEATURES.TRANSLATION]: {
        headline: 'Expand your reach with translations',
        renderDescription: () =>
            'Translate your videos into more languages to reach a global audience. More translations available on our Professional plan and up.',
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/website-assets/undraw_around_the_world_re_rb1p.svg',
        call_to_action: FEATURE_LOCK_CTA.VIEW_PRICING,
        modal_style: FEATURE_LOCK_MODAL_STYLE.VERTICAL,
    },

    /**
     * AI Voiceover
     */
    [LOCKED_FEATURES.GENERATE_SCRIPT_CONTROLS]: {
        headline: 'Unlock our most advanced AI',
        renderDescription: () => (
            <div className="m-l5-0">
                Refine your storytelling with more accurate and controlled AI scripts.
                <div>
                    <div className="mt-l5-16">
                        <b>AI features you get when you upgrade</b>
                    </div>
                    <div className="d-flex flex-row mt-l5-8">
                        <i className="material-symbols-outlined icon-l5 icon-l5--small mt-l5-4 me-l5-8">
                            tune
                        </i>
                        <span>
                            Craft polished video scripts with enhanced <b>duration</b> and{' '}
                            <b>editing controls.</b>
                        </span>
                    </div>
                    <div className="d-flex flex-row mt-l5-8">
                        <i className="material-symbols-outlined icon-l5 icon-l5--small mt-l5-4 me-l5-8">
                            record_voice_over
                        </i>
                        <span>
                            Access <b>40+ lifelike AI Voices</b> and{' '}
                            <b>up to {MAX_AI_VOICEOVER_WORD_COUNT} words</b> for longer videos.
                        </span>
                    </div>
                    <div className="d-flex flex-row mt-l5-8">
                        <i className="material-symbols-outlined icon-l5 icon-l5--small mt-l5-4 me-l5-8">
                            photo_library
                        </i>
                        <span>
                            Create videos faster, with <b>better media</b> and{' '}
                            <b>more varied scene recommendations</b>
                        </span>
                    </div>
                </div>
            </div>
        ),
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/upsell_modals/Modal9-UnlockAdvancedAI%20-%20CROPPED.png',
        call_to_action: FEATURE_LOCK_CTA.VIEW_PRICING,
        modal_style: FEATURE_LOCK_MODAL_STYLE.HORIZONTAL,
        modal_size: MODAL_SIZES.LG,
    },
    [LOCKED_FEATURES.AI_VOICEOVER_MINUTES_LIMIT]: {
        headline: "You've run out of AI voiceover minutes for this cycle",
        renderDescription: () => (
            <div>
                Additional AI voiceover minutes <b>are available on an upgraded plan.</b>
            </div>
        ),
        image_url: 'https://cdn.lumen5.com/lumen5-site-images/Video-Minutes_v2%201.png',
        call_to_action: FEATURE_LOCK_CTA.VIEW_PRICING,
        modal_style: FEATURE_LOCK_MODAL_STYLE.VERTICAL,
    },
    [LOCKED_FEATURES.EDIT_AI_VOICEOVER]: {
        headline: 'Edit your AI voiceover',
        renderDescription: () => (
            <div>
                Make edits to your script on the fly and automatically update your AI voiceover.{' '}
                <b>Available with upgraded plans.</b>
            </div>
        ),
        image_url: 'https://cdn.lumen5.com/lumen5-site-images/Edit-AI-VO_v2%201.png',
        call_to_action: FEATURE_LOCK_CTA.VIEW_PRICING,
        modal_style: FEATURE_LOCK_MODAL_STYLE.VERTICAL,
    },
    [LOCKED_FEATURES.MAX_VIDEO_DURATION_LIMIT]: {
        headline: 'Your video is too long',
        renderDescription: () => (
            <div>
                Create videos longer than 2 minutes <b>on an upgraded plan.</b>
            </div>
        ),
        image_url: 'https://cdn.lumen5.com/lumen5-site-images/Video-Minutes_v2%201.png',
        call_to_action: FEATURE_LOCK_CTA.VIEW_PRICING,
        modal_style: FEATURE_LOCK_MODAL_STYLE.VERTICAL,
    },
    /**
     * Teams
     */
    [LOCKED_FEATURES.TEAM_MEMBERS_ENTERPRISE]: {
        headline: 'Add more team members',
        renderDescription: () =>
            'Looks like your team is growing! Contact your account executive to add more team members.',
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/upsell_modals/Modal6-AddTeamMembers.png',
        call_to_action: FEATURE_LOCK_CTA.CONFIRMATION,
        modal_style: FEATURE_LOCK_MODAL_STYLE.VERTICAL,
    },
    [LOCKED_FEATURES.TEAM_MEMBERS]: {
        headline: 'Introduce us to your team!',
        renderDescription: () =>
            'Our team features make it easy to scale up video creation and collaborate. Contact us to check out our Enterprise plans!',
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/upsell_modals/Modal6-AddTeamMembers.png',
        call_to_action: FEATURE_LOCK_CTA.CONTACT_US,
        modal_style: FEATURE_LOCK_MODAL_STYLE.VERTICAL,
    },
    [LOCKED_FEATURES.TEAM_MEMBERS_LIMIT]: {
        headline: 'Elevate your team’s features with our Enterprise plan',
        renderDescription: () =>
            'Invite your team and unlock advanced controls like roles, approvals, SSO, and more!',
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/upsell_modals/Modal6-AddTeamMembers.png',
        call_to_action: FEATURE_LOCK_CTA.CONTACT_US,
        modal_style: FEATURE_LOCK_MODAL_STYLE.VERTICAL,
    },
    /**
     * Dashboard
     */
    [LOCKED_FEATURES.VIDEO_LIMIT]: {
        headline: "You've reached your free video limit",
        renderDescription: () =>
            'Free accounts can create up to 5 videos per month. Creating more videos is possible on an upgraded plan.',
        image_url: 'https://cdn.lumen5.com/lumen5-site-images/No-Videos_v2%201.png',
        call_to_action: FEATURE_LOCK_CTA.VIEW_PRICING,
        modal_style: FEATURE_LOCK_MODAL_STYLE.VERTICAL,
    },
    /**
     * Custom colors and fonts publish button upsell
     */
    [LOCKED_FEATURES.CANT_PUBLISH_VIDEO_CUSTOM_COLOR]: {
        headline: 'Your video contains custom colors',
        renderDescription: () => 'To publish your video, upgrade your plan.',
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/upsell_modals/ModalB2-CustomizeColors.png',
        call_to_action: FEATURE_LOCK_CTA.VIEW_PRICING,
        modal_style: FEATURE_LOCK_MODAL_STYLE.VERTICAL,
    },
    [LOCKED_FEATURES.CANT_PUBLISH_VIDEO_CUSTOM_FONT]: {
        headline: 'Your video contains custom fonts',
        renderDescription: () => 'To publish your video, upgrade your plan.',
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/upsell_modals/ModalB5-CustomizeFonts.png',
        call_to_action: FEATURE_LOCK_CTA.VIEW_PRICING,
        modal_style: FEATURE_LOCK_MODAL_STYLE.VERTICAL,
    },
    [LOCKED_FEATURES.CANT_PUBLISH_VIDEO_CUSTOM_COLOR_AND_FONT]: {
        headline: 'Your video contains custom colors and fonts',
        renderDescription: () => 'To publish your video, upgrade your plan.',
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/upsell_modals/ModalB5-CustomizeFonts.png',
        call_to_action: FEATURE_LOCK_CTA.VIEW_PRICING,
        modal_style: FEATURE_LOCK_MODAL_STYLE.VERTICAL,
    },
    [LOCKED_FEATURES.CANT_PUBLISH_AI_AVATAR]: {
        headline: 'Your video contains an AI avatar',
        renderDescription: () => (
            <>
                <b>To publish your video, please upgrade your account</b> or remove your AI avatar.
            </>
        ),
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/upsell_modals/ModalB11-UpgradetoPublishAIAvatar.png',
        call_to_action: FEATURE_LOCK_CTA.VIEW_PRICING,
        modal_style: FEATURE_LOCK_MODAL_STYLE.VERTICAL,
    },
    [LOCKED_FEATURES.AI_AVATAR_SCRIPT_COMPOSER]: {
        headline: 'This is a premium feature',
        renderDescription: () => (
            <>
                Your account currently offers free, preview-only access to AI avatars.{' '}
                <b>Publishing videos with AI avatars is available on an upgraded plan.</b>
            </>
        ),
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/upsell_modals/ModalB12-UpgradetoAccessAIAvatar.png',
        call_to_action: FEATURE_LOCK_CTA.VIEW_PRICING,
        modal_style: FEATURE_LOCK_MODAL_STYLE.VERTICAL,
    },
    /**
     * Audio Enhancement
     */
    [LOCKED_FEATURES.AUDIO_ENHANCEMENT]: {
        headline: 'Clean up and enhance your audio',
        renderDescription: () => (
            <>
                Remove background noise and enhance speech clarity with our audio enhancement.{' '}
                <b>This feature is available on an upgraded plan.</b>
            </>
        ),
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/upsell_modals/Modal-AudioEnhancement.png',
        call_to_action: FEATURE_LOCK_CTA.VIEW_PRICING,
        modal_style: FEATURE_LOCK_MODAL_STYLE.VERTICAL,
    },
    [LOCKED_FEATURES.IMAGE_BACKGROUND_REMOVAL]: {
        headline: 'Unlock easier ways to edit',
        renderDescription: () => (
            <>Transform any image into a flexible creative asset with background removal.</>
        ),
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/upsell_modals/Modal-UnlockMediaEdit.png',
        call_to_action: FEATURE_LOCK_CTA.VIEW_PRICING,
        modal_style: FEATURE_LOCK_MODAL_STYLE.VERTICAL,
    },
    /**
     * Upload Stickers
     */
    [LOCKED_FEATURES.UPLOAD_STICKERS]: {
        headline: 'Upload custom stickers',
        renderDescription: () =>
            "Stick your brand's personality on every video. Upgrade your plan to upload custom stickers.",
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/upsell_modals/ModalB4-GoCustom.png',
        call_to_action: FEATURE_LOCK_CTA.VIEW_PRICING,
        modal_style: FEATURE_LOCK_MODAL_STYLE.VERTICAL,
    },
    [LOCKED_FEATURES.AI_IMAGE_GENERATION_MODELS]: {
        headline: 'Unlock more AI features',
        renderDescription: () => (
            <div className="m-l5-0">
                Access premium AI models and expand your creative toolkit.
                <div>
                    <div className="mt-l5-16">
                        <b>AI features you get when you upgrade</b>
                    </div>
                    <div className="d-flex flex-row mt-l5-8">
                        <i className="material-symbols-outlined icon-l5 icon-l5--small mt-l5-4 me-l5-8">
                            settings
                        </i>
                        <span>
                            Expand your creative options for generated visuals and access AI credits
                            for video clips
                        </span>
                    </div>
                    <div className="d-flex flex-row mt-l5-8">
                        <i className="material-symbols-outlined icon-l5 icon-l5--small mt-l5-4 me-l5-8">
                            tune
                        </i>
                        <span>
                            Craft polished video scripts with enhanced <b>duration</b> and{' '}
                            <b>editing controls.</b>
                        </span>
                    </div>
                    <div className="d-flex flex-row mt-l5-8">
                        <i className="material-symbols-outlined icon-l5 icon-l5--small mt-l5-4 me-l5-8">
                            record_voice_over
                        </i>
                        <span>
                            Access <b>40+ lifelike AI Voices</b> and{' '}
                            <b>up to {MAX_AI_VOICEOVER_WORD_COUNT} words</b> for longer videos.
                        </span>
                    </div>
                    <div className="d-flex flex-row mt-l5-8">
                        <i className="material-symbols-outlined icon-l5 icon-l5--small mt-l5-4 me-l5-8">
                            photo_library
                        </i>
                        <span>
                            Create videos faster, with <b>premium media</b> and{' '}
                            <b>more varied scene recommendations</b>
                        </span>
                    </div>
                </div>
            </div>
        ),
        image_url:
            'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/upsell_modals/Modal9-UnlockAdvancedAI%20-%20CROPPED.png',
        call_to_action: FEATURE_LOCK_CTA.VIEW_PRICING,
        modal_style: FEATURE_LOCK_MODAL_STYLE.HORIZONTAL,
        modal_size: MODAL_SIZES.LG,
    },
};

type PropsFromRedux = ConnectedProps<typeof connector>;

function featureInFeatureLockData(modalContext: ImmutableMap<FeatureLockModalContext> | null) {
    const featureName = modalContext?.get('featureName');
    if (!featureName) return null;
    return !!FEATURE_LOCK_MODAL_DATA[featureName];
}

function getFieldFromFeatureLockData<K extends keyof FEATURE_LOCK_DATA>(
    modalContext: ImmutableMap<FeatureLockModalContext> | null,
    field_name: K,
    default_value: FEATURE_LOCK_DATA[K],
): FEATURE_LOCK_DATA[K] {
    if (!featureInFeatureLockData(modalContext)) {
        return default_value;
    }
    const featureName = modalContext?.get('featureName');
    if (!featureName) throw new Error('Feature name is required to show the feature lock modal');
    return FEATURE_LOCK_MODAL_DATA[featureName][field_name] ?? default_value;
}

const getAnalyticsContext = (modalContext: ImmutableMap<FeatureLockModalContext> | null) => {
    return `${modalContext?.get('featureName')} feature lock modal`;
};

const getHeadline = (modalContext: ImmutableMap<FeatureLockModalContext> | null) => {
    return getFieldFromFeatureLockData(modalContext, 'headline', '');
};

const FeatureLockModalPresentation = React.memo(function FeatureLockModalPresentation({
    modalContext,
    showing,
    hideModal,
}: PropsFromRedux) {
    const [showHubspotMeetingModal, setShowHubspotMeetingModal] = React.useState(false);

    const getDescription = React.useCallback(() => {
        const renderDescription = getFieldFromFeatureLockData(
            modalContext,
            'renderDescription',
            () => '',
        );
        return renderDescription({
            modalContext,
            showing,
            hideModal,
        });
    }, [modalContext, showing, hideModal]);

    const getImageUrl = React.useCallback(() => {
        return getFieldFromFeatureLockData(modalContext, 'image_url', PLACEHOLDER_IMAGE_URL);
    }, [modalContext]);

    const getCallToAction = React.useCallback(() => {
        return getFieldFromFeatureLockData(
            modalContext,
            'call_to_action',
            FEATURE_LOCK_CTA.VIEW_PRICING,
        );
    }, [modalContext]);

    const getModalStyle = React.useCallback(() => {
        return getFieldFromFeatureLockData(
            modalContext,
            'modal_style',
            FEATURE_LOCK_MODAL_STYLE.VERTICAL,
        );
    }, [modalContext]);

    const getModalSize = React.useCallback(() => {
        return getFieldFromFeatureLockData(modalContext, 'modal_size', MODAL_SIZES.DEFAULT);
    }, [modalContext]);

    const getHubSpotMeetingLink = React.useCallback(() => {
        const featureName = modalContext?.get('featureName');
        if (
            featureName === LOCKED_FEATURES.TEAM_MEMBERS ||
            featureName === LOCKED_FEATURES.TEAM_MEMBERS_LIMIT ||
            featureName === LOCKED_FEATURES.TEAM_MEMBERS_ENTERPRISE
        ) {
            return 'https://meetings.hubspot.com/lumen5/learn-more-about-team-features';
        }
        return HUBSPOT_BOOK_CHAT_LINK;
    }, [modalContext]);

    const onHidden = React.useCallback(() => {
        if (Immutable.Map.isMap(modalContext)) {
            const onClose = modalContext.get('onClose');
            if (typeof onClose === 'function') {
                onClose();
            }
        }
        hideModal(); // actually close the modal via redux
    }, [modalContext, hideModal]);

    const toggleHubspotMeetingModalVisibility = React.useCallback(
        (isVisible = false) => {
            onHidden();
            setShowHubspotMeetingModal(isVisible);

            if (isVisible) {
                // open hubspot meeting modal
                let scriptTag = document.getElementById(
                    HUBSPOT_MEETING_SCRIPT_TAG_ID,
                ) as HTMLScriptElement;

                if (scriptTag) {
                    scriptTag.remove();
                }
                scriptTag = document.createElement('script');
                scriptTag.setAttribute('id', HUBSPOT_MEETING_SCRIPT_TAG_ID);
                scriptTag.src = HUBSPOT_MEETING_SCRIPT_TAG_SRC;
                document.body.appendChild(scriptTag);

                analytics.track('Let_chat');
            }
        },
        [onHidden],
    );

    const ctaClicked = React.useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();

            const ctaHandlers: Record<FEATURE_LOCK_CTA, () => void> = {
                [FEATURE_LOCK_CTA.VIEW_PRICING]: () => {
                    window.open(Urls.PRICING, '_blank');
                },
                [FEATURE_LOCK_CTA.CONTACT_US]: () => {
                    toggleHubspotMeetingModalVisibility(true);
                },
                [FEATURE_LOCK_CTA.CONFIRMATION]: onHidden,
            };

            const func = ctaHandlers[getCallToAction()];

            func();

            const context = getAnalyticsContext(modalContext);
            trackUpsellClicked(context, {
                subCategory: modalContext?.get('subCategory'),
            });
        },
        [getCallToAction, toggleHubspotMeetingModalVisibility, onHidden, modalContext],
    );

    const renderContent = React.useCallback(() => {
        const descriptionNode = getDescription();
        let onClickSecondaryCta: VoidFunction | undefined;
        let secondaryCtaText: string | undefined;
        if (modalContext) {
            onClickSecondaryCta = modalContext.get('onClickSecondaryCta');
            secondaryCtaText = modalContext.get('secondaryCtaText');
        }
        if (getModalStyle() === FEATURE_LOCK_MODAL_STYLE.HORIZONTAL) {
            return (
                <div className="m-l5-0 px-l5-16 mx-l5-8 mb-l5-48">
                    <div className="d-flex flex-row">
                        <div className="text-block">
                            <h4 className="mt-l5-8 mb-l5-4">{getHeadline(modalContext)}</h4>
                            <div className="description color-grey-800 mb-l5-32">
                                {descriptionNode}
                            </div>
                        </div>
                        <div className="image d-flex align-items-center justify-content-center">
                            <img
                                className="w-100 object-fit-contain"
                                src={getImageUrl()}
                                alt="Feature preview"
                                loading="lazy"
                            />
                        </div>
                    </div>
                    <PrimaryButton
                        text={FEATURE_LOCK_MODAL_CTA_TEXT[getCallToAction()]}
                        onClick={ctaClicked}
                    />
                </div>
            );
        } else {
            return (
                <div className="d-flex flex-column align-items-center mb-l5-24">
                    <div className="image d-flex align-items-center">
                        <img src={getImageUrl()} alt="Feature preview" loading="lazy" />
                    </div>
                    <h4 className="mt-l5-8 mb-l5-4 text-center">{getHeadline(modalContext)}</h4>
                    <div className="description color-grey-800 text-center mb-l5-32">
                        {descriptionNode}
                    </div>
                    <PrimaryButton
                        text={FEATURE_LOCK_MODAL_CTA_TEXT[getCallToAction()]}
                        onClick={ctaClicked}
                    />
                    {secondaryCtaText && (
                        <PrimaryButton
                            className="mt-l5-16"
                            text={secondaryCtaText}
                            variant={BUTTON_VARIANTS.LINK}
                            onClick={onClickSecondaryCta}
                        />
                    )}
                </div>
            );
        }
    }, [getDescription, modalContext, getModalStyle, getImageUrl, getCallToAction, ctaClicked]);

    // Track upsell_displayed when modal becomes visible
    React.useEffect(() => {
        if (showing) {
            analytics.track('upsell_displayed', {
                context: getAnalyticsContext(modalContext),
                subCategory: modalContext?.get('subCategory'),
            });
        }
    }, [showing, modalContext]);

    return (
        <>
            <Modal
                showing={showing}
                size={getModalSize()}
                onHidden={onHidden}
                className={classNames('feature-lock-modal', getModalStyle().toLowerCase())}
            >
                {showing && renderContent()}
            </Modal>
            {/* Hubspot Meeting Schedule Modal */}
            <Modal
                showing={showHubspotMeetingModal}
                size={MODAL_SIZES.LG}
                title={'Schedule a time with us'}
                onHidden={() => {
                    toggleHubspotMeetingModalVisibility(false);
                }}
            >
                <div
                    className="meetings-iframe-container"
                    data-src={`${getHubSpotMeetingLink()}?embed=true`}
                ></div>
            </Modal>
        </>
    );
});

const mapStateToProps = (state: RootState) => ({
    modalContext: getModalContext<FeatureLockModalContext>(state),
    showing: isFeatureLockModalShowing(state),
});

const mapDispatchToProps = (dispatch: AppDispatch) => {
    return bindActionCreators(
        {
            hideModal,
        },
        dispatch,
    );
};

const connector = connect(mapStateToProps, mapDispatchToProps);

const FeatureLockModal = connector(FeatureLockModalPresentation);

export default FeatureLockModal;
